import React, { useEffect, useState } from 'react'

import * as R from 'ramda'
import i18n from 'i18next'

import Badge from '../../../UniformComponents/Badge'
import HoverInfo from '../../../UniformComponents/HoverInfo'

import './compound.scss'

const getSegmentStyle = (score, min, max, numberOfSegments) => {
    const segmentSize = (max - min) / numberOfSegments
    if (score < min + segmentSize) return 'error'
    if (numberOfSegments > 2 && score < min + 2 * segmentSize) return 'warning'
    return 'success'
}

const SubComponent = ({
    name, score, min, max,
}, numberOfSegments, kpiType) => {
    if (R.isNil(min) || R.isNil(max)) return null

    const width = (score - min) / (max - min) * 100
    const type = getSegmentStyle(score, min, max, numberOfSegments)

    return (
        <div className="dependency">
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }} className="dependencyLabel">
                    {i18n.t(`dashboard.compound.${kpiType}.${name}.label`)}
                    {' '}
                    <HoverInfo
                        desc={i18n.t(`dashboard.compound.${kpiType}.${name}`)}
                        dir="right"
                    >
                        <i className="info-icon fa-solid fa-circle-question" />
                    </HoverInfo>
                </div>
                <div className="dependencyValue">
                    <span style={{ fontWeight: 700 }}>{score.toFixed(1)}</span>
                    {' '}
                    /
                    <span style={{ fontWeight: 600, fontSize: '11px', color: '#8B99A7' }}>{max}</span>
                </div>
            </div>
            <div
                className="dependency__scale"
            >
                <span
                    className={`dependency__progress ${type}`}
                    style={{
                        width: `${width}%`,
                    }}
                />
            </div>
        </div>
    )
}

const Scale = ({ min, max, value }, left, right) => {
    const leftOffset = (value - min) / (max - min) * 100
    return (
        <div
            className="scale"
        >
            <div
                className="scale__gradient"
            >
                <div
                    className="scale__pointer"
                    style={{
                        left: `calc(${leftOffset}% - 11px)`,
                    }}
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {Array.from({ length: max }, (_, i) => (
                    <span>{i + min}</span>
                ))}

            </div>
            <div
                className="scale__labels"
            >
                <span className="error">{left}</span>
                <span className="success">{right}</span>
            </div>
        </div>
    )
}

const segmentsECI = {
    error: 'Lagging',
    warning: 'Optimist',
    success: 'Accomplished',
}

const segmentsEEI = {
    error: 'Sceptic',
    warning: 'Lagging',
    success: 'Optimist',
}

const getAdvancedSegmentStyle = (value, segments) => (
    segments.reduce((segmentDetail, segment) => {
        if (segment.min <= value) return segment
        return segmentDetail
    }, {})
)

const Score = (value, widget, numberOfSegments, advancedSegments) => {
    const { min, max, kpiType } = widget
    const advancedSegementDetail = advancedSegments
        ? getAdvancedSegmentStyle(value, advancedSegments)
        : undefined

    const type = advancedSegementDetail
        ? advancedSegementDetail.type
        : getSegmentStyle(value, min, max, numberOfSegments)

    let position = 'Top'

    if (type === 'error') position = 'Bottom'
    else if (type === 'warning') position = 'Middle'

    return (
        <div
            className="score"
        >
            <div className={`score__value ${type}`}>{value.toFixed(2)}</div>
            <div style={{
                display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center',
            }}
            >
                <Badge
                    isRounded
                    type={type}
                >
                    {
                        advancedSegementDetail
                            ? advancedSegementDetail.label
                            : ((kpiType === 'EEI' ? segmentsEEI : segmentsECI)[type])
                    }
                </Badge>
                {/* <div style={{ color: '#8B99A7', fontSize: '11px' }}>
                    {position}
                    {' '}
                    1/
                    {numberOfSegments}
                    {' '}
                    of exhibitors
                </div> */}
            </div>
        </div>
    )
}

const CompoundKPI = ({
    chartData, widget, scale, collapsible, advancedSegments,
}) => {
    const [showCollapsed, setShowCollapsed] = useState(false)
    const { dependencies, kpiType } = widget
    const numberOfSegments = 3
    const defaultSegments = (kpiType === 'EEI' ? segmentsEEI : segmentsECI)
    const segments = advancedSegments
        ? { error: advancedSegments[0].label, success: advancedSegments[advancedSegments.length - 1].label }
        : defaultSegments
    const value = chartData[0]?.value ?? 0
    return (
        <div
            className="compoundKPI"
        >
            {Score(value, widget, numberOfSegments, advancedSegments)}
            {Scale({ min: scale?.min ?? 1, max: scale?.max ?? 5, value }, segments.error, segments.success)}
            {dependencies && dependencies.length && (!collapsible || showCollapsed)
                ? (
                    <>
                        <div
                            style={{
                                fontWeight: 700,
                                color: '#8B99A7',
                                textTransform: 'uppercase',
                                fontSize: '12px',
                            }}
                        >
                            Average by question
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            {dependencies.map((dependency) => (
                                SubComponent(dependency, numberOfSegments, kpiType)
                            ))}
                        </div>
                    </>
                )
                : null}
            {collapsible ? (
                <div className="viewDetails" onClick={() => setShowCollapsed(!showCollapsed)}>
                    {showCollapsed ? 'Hide Details' : 'View Details'}
                    <i className={`fa-regular ${showCollapsed ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </div>
            )
                : null}
        </div>
    )
}

export default CompoundKPI
